/**
 * Shared performance utilities for the full-screen canvas event overlays
 * (Snow/Petal/Leaf). The overlays redraw the entire viewport every frame, so
 * on low-spec devices they can starve the app's own frame budget. Every
 * overlay therefore: renders at capped DPR and FPS, blits pre-rasterized
 * sprite canvases instead of SVG images, and adapts its particle count to the
 * measured frame cadence — stopping (and hiding the canvas) entirely when the
 * device clearly cannot keep up.
 */

/** Decorative translucent particles don't benefit from retina pixel density. */
export const OVERLAY_MAX_DPR = 1

/** Slow-falling particles stay smooth at 30fps; halves all per-frame work. */
export const OVERLAY_TARGET_FPS = 30

/** Quantize a random scale to discrete steps so sprite rasters stay cacheable. */
export function bucketScale(scale: number, step = 0.25): number {
  return Math.max(step, Math.round(scale / step) * step)
}

/**
 * Cache of pre-rasterized sprites. Canvas-to-canvas drawImage is a cheap GPU
 * blit, unlike SVG images which browsers may re-rasterize on every draw and
 * for every distinct draw size.
 */
export function createSpriteCache(img: HTMLImageElement) {
  const cache = new Map<string, HTMLCanvasElement>()
  return (w: number, h: number): HTMLCanvasElement => {
    const width = Math.max(1, Math.round(w))
    const height = Math.max(1, Math.round(h))
    const key = `${width}x${height}`
    let sprite = cache.get(key)
    if (!sprite) {
      sprite = document.createElement('canvas')
      sprite.width = width
      sprite.height = height
      sprite.getContext('2d')?.drawImage(img, 0, 0, width, height)
      cache.set(key, sprite)
    }
    return sprite
  }
}

/** Returns true once per target interval; feed it each rAF timestamp. */
export function createFrameGate(minIntervalMs: number) {
  let lastAccepted = Number.NaN
  return (now: number): boolean => {
    if (now - lastAccepted < minIntervalMs - 1) return false
    lastAccepted = now
    return true
  }
}

export interface AdaptiveQuality {
  readonly count: number
  readonly stopped: boolean
  record(workMs: number, frameIntervalMs: number): void
}

/**
 * Degrade the particle count when frames are expensive or late, recover when
 * cheap, and stop outright when the device cannot hold even ~10fps. Thresholds
 * assume OVERLAY_TARGET_FPS=30 (33ms budget): 50ms = visibly struggling,
 * 100ms = slideshow.
 */
export function createAdaptiveQuality(
  initialCount: number,
  floorCount = 6,
): AdaptiveQuality {
  const SAMPLES = 20
  let count = Math.max(floorCount, initialCount)
  let stopped = false
  let workTotal = 0
  let intervalTotal = 0
  let samples = 0

  return {
    get count() {
      return stopped ? 0 : count
    },
    get stopped() {
      return stopped
    },
    record(workMs: number, frameIntervalMs: number) {
      if (stopped) return
      workTotal += workMs
      intervalTotal += frameIntervalMs
      samples += 1
      if (samples < SAMPLES) return

      const avgWork = workTotal / samples
      const avgInterval = intervalTotal / samples
      workTotal = 0
      intervalTotal = 0
      samples = 0

      if (avgWork > 16 || avgInterval > 100) {
        stopped = true
      } else if (avgWork > 8 || avgInterval > 50) {
        count = Math.max(floorCount, Math.floor(count * 0.7))
      } else if (avgWork < 3.5 && avgInterval < 40 && count < initialCount) {
        count = Math.min(initialCount, Math.ceil(count * 1.15))
      }
    },
  }
}
